/**
 * Family composition rules (#127, D1, D3, A1, Q1, Q5), shared by the
 * registration UI, submit-membership-application, the admin family tools and
 * member requests, so every path enforces the same limits on the server.
 */
import { isJuniorForYear } from './membership';
import { FAMILY_MAX_ADULTS, FAMILY_MAX_SIZE, Household } from './fees';

export interface FamilyPerson {
  dateOfBirth?: string | null;
  /** The family primary (must be an adult, Q1). */
  isPrimary?: boolean;
  /** Rows not active are left out of counts (renewal, pricing). Default true. */
  isActive?: boolean | null;
}

/** Counts adults and juniors for `year` with the club age rule; inactive rows excluded. */
export function householdFromMembers(members: FamilyPerson[], year: number): Household {
  let adults = 0;
  let juniors = 0;
  for (const m of members) {
    if (m.isActive === false) continue;
    if (isJuniorForYear(m.dateOfBirth, year)) juniors += 1;
    else adults += 1;
  }
  return { adults, juniors };
}

export type CompositionResult =
  | { ok: true; household: Household }
  | { ok: false; error: 'TOO_MANY_ADULTS' | 'TOO_MANY_PEOPLE' | 'PRIMARY_NOT_ADULT' | 'MISSING_DOB'; message: string; household: Household };

/**
 * Validates a family (primary included) for `year`: at most 2 adults (always,
 * A1) and 10 people (D3; an admin may exceed 10 with a reason), and the
 * primary, if any is marked, must be an adult under the rule (Q1). Every
 * person needs a date of birth.
 */
export function validateFamilyComposition(
  members: FamilyPerson[],
  year: number,
  options: { adminOverrideReason?: string | null } = {},
): CompositionResult {
  const active = members.filter((m) => m.isActive !== false);
  const household = householdFromMembers(active, year);
  if (active.some((m) => !m.dateOfBirth)) {
    return { ok: false, error: 'MISSING_DOB', message: 'Every family member needs a date of birth.', household };
  }
  const primary = active.find((m) => m.isPrimary);
  if (primary && isJuniorForYear(primary.dateOfBirth, year)) {
    return { ok: false, error: 'PRIMARY_NOT_ADULT', message: 'The family primary must be an adult (17 or over on 1 January).', household };
  }
  if (household.adults > FAMILY_MAX_ADULTS) {
    return {
      ok: false,
      error: 'TOO_MANY_ADULTS',
      message: `A family can have at most ${FAMILY_MAX_ADULTS} adults. A third adult can take an individual membership.`,
      household,
    };
  }
  const size = household.adults + household.juniors;
  const override = (options.adminOverrideReason || '').trim();
  if (size > FAMILY_MAX_SIZE && !override) {
    return {
      ok: false,
      error: 'TOO_MANY_PEOPLE',
      message: `A family can have at most ${FAMILY_MAX_SIZE} people. Please contact the club about larger families.`,
      household,
    };
  }
  return { ok: true, household };
}
